import { FunctionComponent } from 'react';
import clsx from 'clsx';

import {
  IconAirplane,
  IconBeer,
  IconCarrot,
  IconChefHat,
  IconCocktail,
  IconFaceCool,
  IconLogoTock,
  IconPeopleFamily,
  IconPlate,
  IconPlateHeart,
  IconTheater,
  IconToGo,
  IconWineGlass,
} from '@tock/icons';
import { useIsSakeIconSetEnabled } from 'omakase/utils/IconMigrationUtil';
import AchievementCircle, {
  variantToClassnameModifier,
} from 'components/profile/achievements/AchievementCircle';
import IconAdventurer from 'components/icons/svgr/badges/IconAdventurer';
import IconClock from 'components/icons/svgr/IconClock';
import IconFineDiner from 'components/icons/svgr/badges/IconFineDiner';
import IconFirstCourse from 'components/icons/svgr/badges/IconFirstCourse';
import IconGlobetrotter from 'components/icons/svgr/badges/IconGlobetrotter';
import IconGoodTaste from 'components/icons/svgr/badges/IconGoodTaste';
import IconHerbivore from 'components/icons/svgr/badges/IconHerbivore';
import IconHomeChef from 'ui/icons/IconToGoBadge';
import IconHoppedUp from 'components/icons/svgr/badges/IconHoppedUp';
import IconOenophile from 'components/icons/svgr/badges/IconOenophile';
import IconSocialite from 'components/icons/svgr/badges/IconSocialite';
import IconSpirited from 'components/icons/svgr/badges/IconSpirited';
import IconTrendsetter from 'components/icons/svgr/badges/IconTrendsetter';
import IconTrueGourmand from 'components/icons/svgr/badges/IconTrueGourmand';

export interface Props {
  name: string;
  level?: number;
  isAchieved?: boolean;
  className?: string;
  variant?: AchievementCircleVariant;
}

export type AchievementCircleVariant = 'default' | 'large';

const Achievement: FunctionComponent<Props> = ({
  name,
  level,
  isAchieved,
  className,
  variant = 'default',
}) => {
  const sakeIconsEnabled = useIsSakeIconSetEnabled();

  const colorAndIcon = achievementToColorAndIcon[name] || {
    color: 'Achievement_purple',
    legacyIcon: IconClock,
    newIcon: IconClock,
  };
  const modifierClassName = clsx(
    className,
    isAchieved ? colorAndIcon.color : 'Achievement_unachieved'
  );

  const iconClassname = clsx('Achievement-icon', variantToClassnameModifier[variant]);
  return (
    <AchievementCircle className={modifierClassName} level={level || 0} variant={variant}>
      {sakeIconsEnabled ? (
        <colorAndIcon.newIcon className={iconClassname} viewBox="0 0 24 25" />
      ) : (
        <colorAndIcon.legacyIcon className={iconClassname} viewBox="0 0 24 25" />
      )}
    </AchievementCircle>
  );
};

const achievementToColorAndIcon: Dictionary<{
  color: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  legacyIcon: (props: any) => JSX.Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  newIcon: (props: any) => JSX.Element;
}> = {
  Adventurer: {
    color: 'Achievement_adventurer',
    legacyIcon: IconAdventurer,
    newIcon: IconTheater,
  },
  'Fine Diner': {
    color: 'Achievement_fineDiner',
    legacyIcon: IconFineDiner,
    newIcon: IconChefHat,
  },
  'First Course': {
    color: 'Achievement_firstCourse',
    legacyIcon: IconFirstCourse,
    newIcon: IconPlateHeart,
  },
  Globetrotter: {
    color: 'Achievement_globetrotter',
    legacyIcon: IconGlobetrotter,
    newIcon: IconAirplane,
  },
  // Deliberately keeping this so that it won't break when
  'Good Taste': {
    color: 'Achievement_goodTaste',
    legacyIcon: IconGoodTaste,
    newIcon: IconGoodTaste,
  },
  Herbivore: { color: 'Achievement_herbivore', legacyIcon: IconHerbivore, newIcon: IconCarrot },
  'Hopped Up': { color: 'Achievement_hoppedUp', legacyIcon: IconHoppedUp, newIcon: IconBeer },
  Oenophile: { color: 'Achievement_oenophile', legacyIcon: IconOenophile, newIcon: IconWineGlass },
  Socialite: {
    color: 'Achievement_socialite',
    legacyIcon: IconSocialite,
    newIcon: IconPeopleFamily,
  },
  Spirited: { color: 'Achievement_spirited', legacyIcon: IconSpirited, newIcon: IconCocktail },
  'Star Collector': {
    color: 'Achievement_goodTaste',
    legacyIcon: IconGoodTaste,
    newIcon: IconGoodTaste,
  },
  Trendsetter: {
    color: 'Achievement_trendsetter',
    legacyIcon: IconTrendsetter,
    newIcon: IconFaceCool,
  },
  'True Gourmand': {
    color: 'Achievement_trueGourmand',
    legacyIcon: IconTrueGourmand,
    newIcon: IconPlate,
  },
  'Home Chef': { color: 'Achievement_homeChef', legacyIcon: IconHomeChef, newIcon: IconToGo },
  'Tock Star Engineer': {
    color: 'Achievement_adventurer',
    newIcon: IconLogoTock,
    legacyIcon: IconLogoTock,
  },
};

export default Achievement;
